import axios from 'axios';

const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token and tenant ID on every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('smalblu_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const tenantId = localStorage.getItem('smalblu_active_tenant');
  if (tenantId) {
    config.headers['X-Tenant-Id'] = tenantId;
  }
  return config;
});

// Redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('smalblu_token');
      localStorage.removeItem('smalblu_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Monitoring ────────────────────────────────────────────────────────────────

export const monitoringApi = {
  getMetrics: (type = 'compute', hours = 24) =>
    api.get('/monitoring/metrics', { params: { resourceType: type, hours } }),

  getLayerHealth: () =>
    api.get('/monitoring/layers'),

  getUnderutilized: (threshold = 30) =>
    api.get('/monitoring/underutilized', { params: { threshold } }),

  getDatabaseMetrics: () =>
    api.get('/monitoring/database'),

  getDeployments: () =>
    api.get('/monitoring/deployments'),
};

// ── Cost & Analytics ──────────────────────────────────────────────────────────

export const costApi = {
  getDashboard: () =>
    api.get('/cost/dashboard'),

  getReports: (limit = 12) =>
    api.get('/cost/reports', { params: { limit } }),

  getTeamReports: (teamId) =>
    api.get(`/cost/reports/team/${teamId}`),

  getUtilizationReport: () =>
    api.get('/cost/utilization'),

  getRIOpportunities: () =>
    api.get('/cost/ri-opportunities'),

  getDataTransfer: () =>
    api.get('/cost/data-transfer'),

  getCostByTeam: () =>
    api.get('/cost/by-team'),

  getEsgReport: () =>
    api.get('/cost/esg'),

  getSustainability: () =>
    api.get('/cost/sustainability'),
};

// ── Optimization ──────────────────────────────────────────────────────────────

export const optimizationApi = {
  getSummary: () =>
    api.get('/optimization/summary'),

  getRecommendations: (dimension) =>
    api.get('/optimization/recommendations', {
      params: dimension ? { dimension } : {},
    }),

  approve: (id) =>
    api.post(`/optimization/recommendations/${id}/approve`),

  apply: (id) =>
    api.post(`/optimization/recommendations/${id}/apply`),

  getRiskAssessment: (id) =>
    api.get(`/optimization/recommendations/${id}/risk`),

  getLiveAnalysis: () =>
    api.get('/optimization/live-analysis'),
};

// ── AI / NLP ──────────────────────────────────────────────────────────────────

export const aiApi = {
  query: (query, userId) =>
    api.post('/ai/query', { query }, {
      headers: { 'X-User-Id': userId || 'anonymous' },
    }),

  getHistory: (userId) =>
    api.get('/ai/history', { params: { userId } }),

  getCapacityForecast: () =>
    api.get('/ai/capacity-forecast'),

  getAnomalies: () =>
    api.get('/ai/anomalies'),
};

// ── Alerts ────────────────────────────────────────────────────────────────────

export const alertsApi = {
  getAll: () =>
    api.get('/alerts'),

  acknowledge: (id) =>
    api.post(`/alerts/${id}/acknowledge`),

  getCount: () =>
    api.get('/alerts/count'),
};

// ── Settings / Connections ────────────────────────────────────────────────────

export const settingsApi = {
  getConnections: () =>
    api.get('/settings/connections'),

  addConnection: (data) =>
    api.post('/settings/connections', data),

  testConnection: (id) =>
    api.post(`/settings/connections/${id}/test`),

  deleteConnection: (id) =>
    api.delete(`/settings/connections/${id}`),
};

// ── AWS Billing ─────────────────────────────────────────────────────────────

export const awsApi = {
  getFullSummary: () =>
    api.get('/aws/billing/summary'),

  getCurrentMonth: () =>
    api.get('/aws/billing/current-month'),

  getCostByService: (months = 3) =>
    api.get('/aws/billing/by-service', { params: { months } }),

  getDailyTrend: () =>
    api.get('/aws/billing/daily-trend'),

  getForecast: () =>
    api.get('/aws/billing/forecast'),

  getRightsizing: () =>
    api.get('/aws/billing/rightsizing'),

  getUsageBreakdown: () =>
    api.get('/aws/billing/usage-breakdown'),

  getServiceUsage: () =>
    api.get('/aws/billing/service-usage'),

  getCloudWatchOverview: (hours = 24) =>
    api.get('/aws/cloudwatch/overview', { params: { hours } }),

  getActiveServices: () =>
    api.get('/aws/cloudwatch/services'),

  getCloudWatchMetrics: (namespace, hours = 24) =>
    api.get('/aws/cloudwatch/metrics', { params: { namespace, hours } }),

  getEc2Instances: () =>
    api.get('/aws/ec2/instances'),

  getRdsInstances: () =>
    api.get('/aws/rds/instances'),
};

// ── Tenants ──────────────────────────────────────────────────────────────────

export const tenantsApi = {
  getAll: () =>
    api.get('/tenants'),

  getById: (id) =>
    api.get(`/tenants/${id}`),

  create: (data) =>
    api.post('/tenants', data),

  update: (id, data) =>
    api.put(`/tenants/${id}`, data),

  delete: (id) =>
    api.delete(`/tenants/${id}`),

  testAws: (id) =>
    api.post(`/tenants/${id}/test-aws`),
};

// ── Database Optimization (sb-database microservice) ─────────────────────────
const DB_SERVICE_URL = process.env.REACT_APP_DB_SERVICE_URL || 'http://localhost:8081';

const dbApi = axios.create({
  baseURL: `${DB_SERVICE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

dbApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('smalblu_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const dbOptApi = {
  listCustomers: () =>
    dbApi.get('/customers'),

  getMetrics: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/metrics`),

  getSuggestions: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/suggestions`),

  getConnectionPool: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/connection-pool`),

  getSlowQueries: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/slow-queries`),

  getTableHealth: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/table-health`),

  getCacheStats: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/cache-stats`),

  refresh: (customerId) =>
    dbApi.post(`/customers/${customerId}/database-metrics/refresh`),

  health: (customerId) =>
    dbApi.get(`/customers/${customerId}/database-metrics/health`),
};

// ── Cloud Connections (per-tenant AWS role-ARN / static-keys) ───────────────
export const cloudConnectionsApi = {
  get:            ()                 => api.get('/cloud-connections'),
  saveRole:       (payload)          => api.post('/cloud-connections', payload),
  saveStaticKeys: (payload)          => api.post('/cloud-connections/static-keys', payload),
  test:           ()                 => api.post('/cloud-connections/test'),
  disconnect:     ()                 => api.delete('/cloud-connections'),
  templateUrl:    ()                 => (process.env.REACT_APP_API_URL || '/api/v1') + '/cloud-connections/cloudformation-template',
};

// ── Audit + Compliance ─────────────────────────────────────────────────────
export const auditApi = {
  list:            (params = {})       => api.get('/audit', { params }),
  exportCsvUrl:    ()                  => (process.env.REACT_APP_API_URL || '/api/v1') + '/audit/export.csv',
  runRetention:    ()                  => api.post('/audit/retention/run-now'),
  integrity:       (limit = 5000)      => api.get('/audit/integrity', { params: { limit } }),
  complianceUrl:   (framework, from, to) => {
    const base = (process.env.REACT_APP_API_URL || '/api/v1') + `/audit/compliance/${framework}`;
    const q = [];
    if (from) q.push(`from=${from}`);
    if (to)   q.push(`to=${to}`);
    return q.length ? `${base}?${q.join('&')}` : base;
  },
};

// ── Notifications (inbox + subscriptions) ──────────────────────────────────
export const notificationsApi = {
  inbox:          (limit = 50)       => api.get('/notifications/inbox', { params: { limit } }),
  unreadCount:    ()                 => api.get('/notifications/inbox/unread-count'),
  markRead:       (id)               => api.post(`/notifications/${id}/mark-read`),
  listSubscriptions: ()              => api.get('/notifications/subscriptions'),
  subscribe:      (payload)          => api.post('/notifications/subscriptions', payload),
  unsubscribe:    (id)               => api.delete(`/notifications/subscriptions/${id}`),
};

export default api;
